import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const AdHocQueryPage = () => {
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState('clients');
  const [selectedFields, setSelectedFields] = useState([]);
  const [filterKey, setFilterKey] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [limit, setLimit] = useState(50);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadEntities(); }, []);
  const loadEntities = async () => {
    try { const res = await api.get('/adhoc-query/entities'); setEntities(res.data); }
    catch { setEntities([]); }
  };

  const currentEntity = entities.find(e => e.name === selectedEntity);

  const toggleField = (field) => {
    setSelectedFields(prev => prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]);
  };

  const handleQuery = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filterKey && filterValue) filters[filterKey] = filterValue;
      const res = await api.post('/adhoc-query', { entity: selectedEntity, filters, fields: selectedFields.length > 0 ? selectedFields : undefined, limit });
      setResults(res.data);
      toast.success(`Found ${res.data.total} record(s)`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Query failed'); }
    setLoading(false);
  };

  const exportCSV = () => {
    if (!results?.results?.length) return;
    const headers = Object.keys(results.results[0]);
    const rows = results.results.map(r => headers.map(h => JSON.stringify(r[h] ?? '')).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${selectedEntity}_query_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <AdminSubPage title="Ad-Hoc Query" breadcrumb={['Admin', 'System', 'Ad-Hoc Query']} backPath="/dashboard/admin/system">
      <div className="space-y-4">
        {/* Query Builder */}
        <div className="bg-white border border-gray-200 p-6" data-testid="query-builder">
          <h3 className="font-semibold text-[#1f1f1f] mb-4">Query Builder</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Entity</Label>
              <Select value={selectedEntity} onValueChange={v => { setSelectedEntity(v); setSelectedFields([]); setFilterKey(''); setResults(null); }}>
                <SelectTrigger data-testid="entity-select"><SelectValue /></SelectTrigger>
                <SelectContent>{entities.map(e => <SelectItem key={e.name} value={e.name}>{e.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Filter By</Label>
              <Select value={filterKey || undefined} onValueChange={v => setFilterKey(v === '__none__' ? '' : v)}>
                <SelectTrigger data-testid="filter-field-select"><SelectValue placeholder="(Optional) Select field" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No filter</SelectItem>
                  {(currentEntity?.fields || []).map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {filterKey && (
              <div><Label>Filter Value</Label><Input value={filterValue} onChange={e => setFilterValue(e.target.value)} placeholder="Enter value..." data-testid="filter-value-input" /></div>
            )}
          </div>

          {/* Field Selection */}
          {currentEntity && (
            <div className="mt-4">
              <Label className="mb-2 block">Select Fields (leave empty for all)</Label>
              <div className="flex flex-wrap gap-2">
                {currentEntity.fields.map(f => (
                  <button key={f} onClick={() => toggleField(f)} className={`px-3 py-1 text-xs border transition-colors ${selectedFields.includes(f) ? 'bg-[#1f1f1f] text-white border-[#1f1f1f]' : 'bg-white text-gray-600 border-gray-300 hover:border-[#C5A059]'}`} data-testid={`field-${f}`}>
                    {f}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-end gap-4 mt-4">
            <div><Label>Limit</Label><Input type="number" value={limit} onChange={e => setLimit(parseInt(e.target.value) || 50)} className="w-24" /></div>
            <Button onClick={handleQuery} disabled={loading} className="bg-[#1f1f1f] text-white" data-testid="run-query-btn">
              <Search className="w-4 h-4 mr-2" />{loading ? 'Running...' : 'Run Query'}
            </Button>
            {results?.results?.length > 0 && (
              <Button variant="outline" onClick={exportCSV} data-testid="export-csv-btn"><Download className="w-4 h-4 mr-2" />Export CSV</Button>
            )}
          </div>
        </div>

        {/* Results */}
        {results && (
          <div className="bg-white border border-gray-200" data-testid="query-results">
            <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
              <span className="text-sm font-medium text-[#1f1f1f]">Results: {results.returned} of {results.total}</span>
              <span className="text-xs text-gray-400 capitalize">Entity: {results.entity}</span>
            </div>
            {results.results.length === 0 ? (
              <div className="text-center py-8 text-gray-400">No records found matching your query.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b">
                    {Object.keys(results.results[0]).map(key => (
                      <th key={key} className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap">{key}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {results.results.map((row, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        {Object.values(row).map((val, vi) => (
                          <td key={vi} className="px-3 py-2 text-sm text-gray-700 max-w-[200px] truncate whitespace-nowrap">
                            {val === null ? <span className="text-gray-300">null</span> : typeof val === 'object' ? JSON.stringify(val) : String(val)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminSubPage>
  );
};
export default AdHocQueryPage;
